/**
 * AgentCapabilityBadges.jsx
 * 
 * Reusable component for displaying agent capability badges.
 * Used across: Marketplace, Agent Detail, My Agents, Analytics
 * 
 * CANONICAL MODEL v1.0: capability_profile is the SOURCE OF TRUTH
 * 
 * EXACTLY 4 PROFILES (DO NOT EXTEND):
 * - text_agent           → 🧠 Text Agent (can accept audio FILES, NEVER wizard)
 * - voice_input_agent    → 🎤 Voice Input (live mic/meeting, wizard only if meeting integration)
 * - voice_output_agent   → 🔊 Voice Output (TTS only, NEVER wizard)
 * - live_voice_s2s_agent → 📞 Live Voice S2S (telephony, ALWAYS wizard)
 * 
 * WIZARD TRIGGER LOGIC:
 *   wizard_required = capability_profile === "live_voice_s2s_agent"
 *     OR (capability_profile === "voice_input_agent" AND requires_live_meeting_integration === true)
 */

import React from 'react';
import {
  Brain,
  LinkSimple,
  Microphone,
  Phone,
  SpeakerHigh,
  Waveform
} from '@phosphor-icons/react';

// ═══════════════════════════════════════════════════════════════════════════════
// CANONICAL CAPABILITY PROFILES (v1.0 - EXACTLY 4 PROFILES)
// ═══════════════════════════════════════════════════════════════════════════════
const CAPABILITY_PROFILES = {
  text_agent: {
    emoji: '🧠',
    label: 'Text Agent',
    description: 'Process text, files, and audio files for analysis',
    color: 'gray',
    bgClass: 'bg-gray-500/20',
    textClass: 'text-gray-400',
    borderClass: 'border-gray-500/30',
    Icon: Brain,
    wizardRequired: false,  // NEVER - even with audio file uploads
    voiceEnabled: false,
    canAcceptAudioFiles: true
  },
  live_voice_s2s_agent: {
    emoji: '📞',
    label: 'Live Voice (S2S)',
    description: 'Real-time phone conversations via Twilio/Telnyx',
    color: 'purple',
    bgClass: 'bg-purple-500/20',
    textClass: 'text-purple-400',
    borderClass: 'border-purple-500/30',
    Icon: Phone,
    wizardRequired: true,  // ALWAYS
    voiceEnabled: true,
    telephonyRequired: true
  },
  voice_output_agent: {
    emoji: '🔊',
    label: 'Voice Output (TTS)',
    description: 'Text-to-speech responses',
    color: 'blue',
    bgClass: 'bg-blue-500/20',
    textClass: 'text-blue-400',
    borderClass: 'border-blue-500/30',
    Icon: SpeakerHigh,
    wizardRequired: false,  // NEVER
    voiceEnabled: true
  },
  voice_input_agent: {
    emoji: '🎤',
    label: 'Voice Input',
    description: 'Live microphone or meeting platform input',
    color: 'teal',
    bgClass: 'bg-teal-500/20',
    textClass: 'text-teal-400',
    borderClass: 'border-teal-500/30',
    Icon: Microphone,
    wizardRequired: false,  // Only if requires_live_meeting_integration
    voiceEnabled: true,
    liveMicSupported: true
  },
  // Backward compatibility aliases
  voice_output_tts_agent: {
    emoji: '🔊',
    label: 'Voice Output (TTS)',
    description: 'Text-to-speech responses',
    color: 'blue',
    bgClass: 'bg-blue-500/20',
    textClass: 'text-blue-400',
    borderClass: 'border-blue-500/30',
    Icon: SpeakerHigh,
    wizardRequired: false,
    voiceEnabled: true
  },
  integration_automation_agent: {
    emoji: '🧠',
    label: 'Text Agent',
    description: 'Process text and files (reclassified)',
    color: 'gray',
    bgClass: 'bg-gray-500/20',
    textClass: 'text-gray-400',
    borderClass: 'border-gray-500/30',
    Icon: Brain,
    wizardRequired: false,
    voiceEnabled: false
  }
};

// Voice-capable profiles for filtering
const VOICE_CAPABLE_PROFILES = [
  'live_voice_s2s_agent',
  'voice_output_agent',
  'voice_input_agent'
];

// ═══════════════════════════════════════════════════════════════════════════════
// LEGACY VOICE CAPABILITIES (Kept for backward compatibility)
// ═══════════════════════════════════════════════════════════════════════════════
const VOICE_CAPABILITIES = {
  live_voice_s2s: {
    emoji: '📞',
    label: 'Live Voice (S2S)',
    description: 'Real-time phone conversations',
    color: 'purple',
    bgClass: 'bg-purple-500/20',
    textClass: 'text-purple-400',
    borderClass: 'border-purple-500/30',
    Icon: Microphone
  },
  voice_output_tts: {
    emoji: '🔊',
    label: 'Voice Output (TTS)',
    description: 'Speaks responses but not conversational',
    color: 'blue',
    bgClass: 'bg-blue-500/20',
    textClass: 'text-blue-400',
    borderClass: 'border-blue-500/30',
    Icon: SpeakerHigh
  },
  voice_input: {
    emoji: '🗣️',
    label: 'Voice Input',
    description: 'Consumes audio/transcripts',
    color: 'teal',
    bgClass: 'bg-teal-500/20',
    textClass: 'text-teal-400',
    borderClass: 'border-teal-500/30',
    Icon: Waveform
  },
  none: {
    emoji: '🧠',
    label: 'Text Agent',
    description: 'Text-only processing',
    color: 'gray',
    bgClass: 'bg-gray-500/20',
    textClass: 'text-gray-400',
    borderClass: 'border-gray-500/30',
    Icon: Brain
  }
};

// Integration definitions
const INTEGRATIONS = {
  microsoft365: {
    emoji: '⚙️',
    label: 'Microsoft 365',
    color: 'blue',
    bgClass: 'bg-blue-500/10',
    textClass: 'text-blue-400',
    borderClass: 'border-blue-500/20'
  },
  google_workspace: {
    emoji: '⚙️',
    label: 'Google',
    color: 'green',
    bgClass: 'bg-green-500/10',
    textClass: 'text-green-400',
    borderClass: 'border-green-500/20'
  },
  salesforce: {
    emoji: '⚙️',
    label: 'Salesforce',
    color: 'cyan',
    bgClass: 'bg-cyan-500/10',
    textClass: 'text-cyan-400',
    borderClass: 'border-cyan-500/20'
  },
  xero: {
    emoji: '⚙️',
    label: 'Xero',
    color: 'indigo',
    bgClass: 'bg-indigo-500/10',
    textClass: 'text-indigo-400',
    borderClass: 'border-indigo-500/20'
  },
  twilio: {
    emoji: '📞',
    label: 'Twilio',
    color: 'red',
    bgClass: 'bg-red-500/10',
    textClass: 'text-red-400',
    borderClass: 'border-red-500/20',
    Icon: Phone
  },
  telnyx: {
    emoji: '📞',
    label: 'Telnyx',
    color: 'emerald',
    bgClass: 'bg-emerald-500/10',
    textClass: 'text-emerald-400',
    borderClass: 'border-emerald-500/20',
    Icon: Phone
  },
  twilio_or_telnyx: {
    emoji: '📞',
    label: 'Twilio or Telnyx',
    color: 'orange',
    bgClass: 'bg-orange-500/10',
    textClass: 'text-orange-400',
    borderClass: 'border-orange-500/20',
    Icon: Phone
  }
};

// Capability mode definitions (legacy support)
const CAPABILITY_MODES = {
  text_only: {
    label: 'Text Only',
    bgClass: 'bg-blue-500/20',
    textClass: 'text-blue-400',
    borderClass: 'border-blue-500/30'
  },
  automation_ready: {
    label: 'Automation Ready',
    bgClass: 'bg-green-500/20',
    textClass: 'text-green-400',
    borderClass: 'border-green-500/30'
  },
  integration_dependent: {
    label: 'Needs Integration',
    bgClass: 'bg-amber-500/20',
    textClass: 'text-amber-400',
    borderClass: 'border-amber-500/30'
  },
  file_enabled: {
    label: 'File Enabled',
    bgClass: 'bg-cyan-500/20',
    textClass: 'text-cyan-400',
    borderClass: 'border-cyan-500/30'
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if a capability_profile is voice-capable
 */
export const isVoiceCapableProfile = (capabilityProfile) => {
  return VOICE_CAPABLE_PROFILES.includes(capabilityProfile);
};

/**
 * Get capability profile config from capability_profile or legacy fields
 */
export const getCapabilityProfileConfig = (capabilityProfile, voiceCapability) => {
  // Try capability_profile first (Source of Truth)
  if (capabilityProfile && CAPABILITY_PROFILES[capabilityProfile]) {
    return CAPABILITY_PROFILES[capabilityProfile];
  }
  
  // Fallback: Map legacy voiceCapability to capability_profile
  if (voiceCapability) {
    const legacyMapping = {
      'live_voice_s2s': CAPABILITY_PROFILES.live_voice_s2s_agent,
      'voice_output_tts': CAPABILITY_PROFILES.voice_output_tts_agent,
      'voice_input': CAPABILITY_PROFILES.voice_input_agent,
      'none': CAPABILITY_PROFILES.text_agent
    };
    if (legacyMapping[voiceCapability]) {
      return legacyMapping[voiceCapability];
    }
  }
  
  // Default to text_agent
  return CAPABILITY_PROFILES.text_agent;
};

/**
 * CapabilityProfileBadge (V2.0 - Source of Truth)
 * Displays the capability profile badge for an agent/template
 * This is the PRIMARY badge component - use this instead of VoiceCapabilityBadge
 */
export const CapabilityProfileBadge = ({ 
  capabilityProfile,
  voiceCapability, // Legacy fallback
  size = 'sm',
  showIcon = true,
  showLabel = true,
  showWizardIndicator = false,
  className = '' 
}) => {
  const config = getCapabilityProfileConfig(capabilityProfile, voiceCapability);
  const { Icon, emoji, label, bgClass, textClass, borderClass, description, wizardRequired } = config;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1 ${bgClass} ${textClass} border ${borderClass} rounded ${sizeClasses[size]} ${className}`}
      title={description}
    >
      {showIcon && (Icon ? <Icon size={size === 'xs' ? 10 : size === 'sm' ? 12 : 14} /> : emoji)}
      {showLabel && <span>{label}</span>}
      {showWizardIndicator && wizardRequired && (
        <span className="ml-1 text-amber-400" title="Requires wizard setup">⚡</span>
      )}
    </span>
  );
};

/**
 * VoiceCapabilityBadge (LEGACY - use CapabilityProfileBadge instead)
 * Displays the voice capability badge for an agent/template
 */
export const VoiceCapabilityBadge = ({ 
  voiceCapability = 'none', 
  size = 'sm',
  showIcon = true,
  showLabel = true,
  className = '' 
}) => {
  const config = VOICE_CAPABILITIES[voiceCapability] || VOICE_CAPABILITIES.none;
  const { Icon, emoji, label, bgClass, textClass, borderClass, description } = config;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  // Don't show badge for text agents unless explicitly requested
  if (voiceCapability === 'none' && !showLabel) {
    return null;
  }
  
  return (
    <span 
      className={`inline-flex items-center gap-1 ${bgClass} ${textClass} border ${borderClass} rounded ${sizeClasses[size]} ${className}`}
      title={description}
    >
      {showIcon && (Icon ? <Icon size={size === 'xs' ? 10 : size === 'sm' ? 12 : 14} /> : emoji)}
      {showLabel && <span>{label}</span>}
    </span>
  );
};

/**
 * IntegrationBadge
 * Displays a single integration badge
 */
export const IntegrationBadge = ({ 
  integration, 
  size = 'sm',
  showIcon = true,
  showLabel = true,
  className = '' 
}) => {
  const config = INTEGRATIONS[integration];
  if (!config) return null;
  
  const { Icon, emoji, label, bgClass, textClass, borderClass } = config;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1 ${bgClass} ${textClass} border ${borderClass} rounded ${sizeClasses[size]} ${className}`}
      title={`Requires ${label}`}
    >
      {showIcon && (Icon ? <Icon size={size === 'xs' ? 10 : size === 'sm' ? 12 : 14} /> : emoji)}
      {showLabel && <span>{label}</span>}
    </span>
  );
};

/**
 * IntegrationBadges
 * Displays all enabled integrations for an agent/template
 */
export const IntegrationBadges = ({ 
  integrations = [], 
  requiredOnly = false,
  maxDisplay = 3,
  size = 'sm',
  className = '' 
}) => {
  if (!integrations || integrations.length === 0) return null;
  
  const displayIntegrations = integrations.slice(0, maxDisplay);
  const remaining = integrations.length - maxDisplay;
  
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {displayIntegrations.map((integration) => (
        <IntegrationBadge 
          key={integration} 
          integration={integration} 
          size={size}
          showLabel={integrations.length <= 2}
        />
      ))}
      {remaining > 0 && (
        <span className={`text-xs px-1.5 py-0.5 bg-gray-500/10 text-gray-400 rounded`}>
          +{remaining} more
        </span>
      )}
    </div>
  );
};

/**
 * CapabilityModeBadge
 * Displays the capability mode badge (text_only, automation_ready, etc.)
 */
export const CapabilityModeBadge = ({ 
  mode = 'text_only', 
  size = 'sm',
  className = '' 
}) => {
  const config = CAPABILITY_MODES[mode] || CAPABILITY_MODES.text_only;
  const { label, bgClass, textClass, borderClass } = config;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  return (
    <span 
      className={`inline-flex items-center ${bgClass} ${textClass} border ${borderClass} rounded ${sizeClasses[size]} ${className}`}
    >
      {label}
    </span>
  );
};

/**
 * AgentCapabilityBadges
 * Comprehensive badge display for an agent/template
 * V2.0: Uses capability_profile as Source of Truth
 */
export const AgentCapabilityBadges = ({
  capabilityProfile, // V2.0 Source of Truth
  voiceCapability,   // Legacy fallback
  capabilityMode,
  integrationsEnabled = [],
  integrationRequirements = {},
  size = 'sm',
  layout = 'horizontal', // 'horizontal' | 'vertical' | 'stacked'
  showCapabilityMode = true,
  showCapabilityProfile = true,
  showVoice = true,  // Kept for legacy but prefer showCapabilityProfile
  showIntegrations = true,
  className = ''
}) => {
  // V2.0: Use capability_profile as primary
  const profileConfig = getCapabilityProfileConfig(capabilityProfile, voiceCapability);
  const isVoice = isVoiceCapableProfile(capabilityProfile);
  
  // Get required integrations
  const requiredIntegrations = integrationRequirements?.required || [];
  const allIntegrations = [...new Set([...requiredIntegrations, ...(integrationsEnabled || [])])];
  
  const layoutClasses = {
    horizontal: 'flex flex-wrap items-center gap-1.5',
    vertical: 'flex flex-col gap-1.5',
    stacked: 'space-y-1.5'
  };
  
  return (
    <div className={`${layoutClasses[layout]} ${className}`}>
      {/* V2.0: Capability Profile Badge (Source of Truth) */}
      {showCapabilityProfile && capabilityProfile && (
        <CapabilityProfileBadge 
          capabilityProfile={capabilityProfile} 
          voiceCapability={voiceCapability}
          size={size} 
        />
      )}
      
      {/* Legacy: Voice Capability Badge (only if no capability_profile) */}
      {showVoice && !capabilityProfile && voiceCapability && voiceCapability !== 'none' && (
        <VoiceCapabilityBadge voiceCapability={voiceCapability} size={size} />
      )}
      
      {/* Capability Mode Badge */}
      {showCapabilityMode && capabilityMode && (
        <CapabilityModeBadge mode={capabilityMode} size={size} />
      )}
      
      {/* Integration Badges */}
      {showIntegrations && allIntegrations.length > 0 && (
        <IntegrationBadges 
          integrations={allIntegrations} 
          size={size}
          maxDisplay={layout === 'horizontal' ? 2 : 5}
        />
      )}
    </div>
  );
};

/**
 * AgentTypeIndicator
 * Shows whether agent is Text, Live Voice, or Voice Output
 * Used in Create Agent flow and template cards
 */
export const AgentTypeIndicator = ({
  agentType = 'text',
  size = 'md',
  showDescription = false,
  className = ''
}) => {
  const types = {
    text: {
      label: 'Text Agent',
      description: 'App-like, instant install',
      emoji: '🧠',
      bgClass: 'bg-gray-500/20',
      textClass: 'text-gray-300',
      borderClass: 'border-gray-500/30'
    },
    live_voice: {
      label: 'Live Voice Agent',
      description: 'Real-time voice with guided setup',
      emoji: '🎙️',
      bgClass: 'bg-purple-500/20',
      textClass: 'text-purple-400',
      borderClass: 'border-purple-500/30'
    },
    voice_output: {
      label: 'Voice Output Agent',
      description: 'Text agent with voice responses',
      emoji: '🔊',
      bgClass: 'bg-blue-500/20',
      textClass: 'text-blue-400',
      borderClass: 'border-blue-500/30'
    }
  };
  
  const config = types[agentType] || types.text;
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };
  
  return (
    <div className={`inline-flex flex-col ${className}`}>
      <span 
        className={`inline-flex items-center gap-2 ${config.bgClass} ${config.textClass} border ${config.borderClass} rounded-lg ${sizeClasses[size]}`}
      >
        <span>{config.emoji}</span>
        <span className="font-medium">{config.label}</span>
      </span>
      {showDescription && (
        <span className="text-xs text-gray-500 mt-1">{config.description}</span>
      )}
    </div>
  );
};

/**
 * RequiresWizardIndicator
 * Shows if template requires guided wizard setup
 */
export const RequiresWizardIndicator = ({
  requiresWizard = false,
  wizardType,
  size = 'sm',
  className = ''
}) => {
  if (!requiresWizard) return null;
  
  const wizardLabels = {
    voice_booking: 'Voice Booking Setup',
    voice_receptionist: 'Receptionist Setup',
    voice_universal: 'Voice Setup',
    integration: 'Integration Setup'
  };
  
  const label = wizardLabels[wizardType] || 'Guided Setup Required';
  
  return (
    <span 
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded ${className}`}
      title="This agent requires guided configuration wizard"
    >
      <LinkSimple size={12} />
      {label}
    </span>
  );
};

// Export all constants for use in other components
export { 
  CAPABILITY_PROFILES,
  VOICE_CAPABLE_PROFILES,
  VOICE_CAPABILITIES, 
  INTEGRATIONS, 
  CAPABILITY_MODES 
};

export default AgentCapabilityBadges;
